import { createContext, useContext, useState, useCallback, useRef } from "react";

const ModalContext = createContext(null);

/**
 * Centralized Modal System - prevents black screens and UI freezes
 * by ensuring only one modal is rendered at a time with proper cleanup.
 * Non-breaking: existing modals continue to work, this context
 * provides a GLOBAL override layer for critical modals.
 */
export function ModalProvider({ children }) {
  const [modalStack, setModalStack] = useState([]); // stack of { id, component, props }
  const idRef = useRef(0);

  const openModal = useCallback((component, props = {}) => {
    const id = ++idRef.current;
    setModalStack(prev => [...prev, { id, component, props }]);
    return id;
  }, []);

  const closeModal = useCallback((id) => {
    setModalStack(prev => {
      if (id !== undefined) {
        return prev.filter(m => m.id !== id);
      }
      // If no id, close the top modal
      return prev.slice(0, -1);
    });
  }, []);

  const closeAll = useCallback(() => {
    setModalStack([]);
  }, []);

  const activeModal = modalStack[modalStack.length - 1] || null;

  return (
    <ModalContext.Provider value={{ openModal, closeModal, closeAll, activeModal, modalStack }}>
      {children}
      {/* Global modal renderer - renders top of stack */}
      {activeModal && (
        <GlobalModalRenderer
          modal={activeModal}
          onClose={() => closeModal(activeModal.id)}
        />
      )}
    </ModalContext.Provider>
  );
}

function GlobalModalRenderer({ modal, onClose }) {
  const { component: Component, props } = modal;
  return <Component {...props} open={true} onOpenChange={(open) => { if (!open) onClose(); }} onClose={onClose} />;
}

export function useModal() {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error("useModal must be used within ModalProvider");
  return ctx;
}
